import { useState, useEffect, useMemo, useCallback } from "react";
import Paper from "@mui/material/Paper";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { toast } from "react-toastify";

import { save } from "./api-presenca";
import PresencaCapituloTable from "../PresencaCapitulo/PresencaCapituloTable";
import { timeFormat } from "../../util/DateUtil";
import TextFieldDate from "../UI/TextFieldDate";
import PresencaAlunoTableSwitch from "../PresencaAluno/PresencaAlunoTableSwitch";
import Select from "../UI/Select";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { findByTdpCodigoAndData } from "../Cronograma/api-cronograma";

// Estilos migrados de `@mui/styles` (deprecado / incompatível com React 18 StrictMode
// e removido no MUI v6) para `sx`, que é a forma recomendada atualmente.
const sx = {
  paper: {
    marginTop: "20px",
    marginBottom: "20px",
    paddingTop: "20px",
    paddingBottom: "20px",
    display: "flex",
    flexDirection: "column",
  },
  closeIcon: {
    marginTop: "20px",
    alignSelf: "flex-end",
  },
  loading: {
    marginRight: "10px",
    marginLeft: "10px",
  },
};

const MESSAGES = {
  DATA_REQUIRED: "Digite a data para carregar os cronogramas.",
  CRONOGRAMA_NOT_FOUND: "Nenhum cronograma encontrado.",
  CRONOGRAMA_LOAD_ERROR: "Erro ao carregar cronogramas.",
  PRESENCA_SAVE_EMPTY: "Não foi possível salvar a presença.",
  PRESENCA_SAVE_ERROR: "Erro inesperado ao salvar a presença.",
  PRESENCA_SAVED: "Salvo com sucesso.",
};

const getCronogramaDescription = (cronograma) => {
  const { ordemAula, horarioInicio, horarioFim } = cronograma.cronogramaAula;
  return `Aula ${ordemAula} : ${timeFormat(horarioInicio)} - ${timeFormat(horarioFim)}`;
};

/**
 * Encapsula o carregamento de cronogramas para uma data/turma (tdp) específica.
 * Isola o estado assíncrono (lista + loading) do componente de formulário,
 * que passa a apenas consumir `list`, `isLoading` e `load`.
 */
function useCronogramas({ tdpCodigo, data }) {
  const [list, setList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // A lista pertence a uma combinação (tdp, data). Se a data mudar, a lista
  // anterior fica inválida e deve ser limpa para não exibir opções erradas.
  useEffect(() => {
    setList([]);
  }, [data]);

  const load = useCallback(async () => {
    if (!data) {
      errorMessage(MESSAGES.DATA_REQUIRED);
      return;
    }
    setIsLoading(true);
    try {
      const result = await findByTdpCodigoAndData(tdpCodigo, data);
      if (!result) {
        errorMessage(MESSAGES.CRONOGRAMA_NOT_FOUND);
        return;
      }
      if (result.error) {
        errorMessage(result.message);
        return;
      }
      setList(result);
    } catch (error) {
      errorMessage(error?.message ?? MESSAGES.CRONOGRAMA_LOAD_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [tdpCodigo, data]);

  return { list, isLoading, load };
}

/**
 * Botão + select de cronograma. Extraído para reduzir o tamanho do JSX
 * principal e deixar explícito o contrato: recebe o cronograma selecionado
 * e a lista disponível, avisa mudanças via `onSelect`.
 */
function CronogramaField({
  selected,
  options,
  isLoading,
  disabled,
  onLoad,
  onSelect,
}) {
  return (
    <>
      <Grid>Cronograma: {selected && getCronogramaDescription(selected)}</Grid>

      {options.length > 0 && (
        <Grid>
          <Select
            label="Cronograma"
            value={selected?.codigo ?? ""}
            list={options}
            onChange={onSelect}
          />
        </Grid>
      )}

      <Grid>
        <Button
          onClick={onLoad}
          fullWidth
          variant="contained"
          color="secondary"
          disabled={isLoading || disabled}
        >
          {isLoading && (
            <CircularProgress sx={sx.loading} color="inherit" size={20} />
          )}
          Carregar Cronogramas
        </Button>
      </Grid>
    </>
  );
}

/** Botão de submit com indicador de carregamento. */
function SubmitButton({ isLoading }) {
  return (
    <Button
      type="submit"
      fullWidth
      variant="contained"
      disabled={isLoading}
      color="primary"
    >
      {isLoading && (
        <CircularProgress sx={sx.loading} color="inherit" size={20} />
      )}
      Salvar
    </Button>
  );
}

export default function PresencaForm({
  presenca,
  setPresenca,
  setPresencas,
  addPresencas,
}) {
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const {
    list: listCronograma,
    isLoading: isLoadingCronograma,
    load: loadCronogramas,
  } = useCronogramas({
    tdpCodigo: presenca?.tdp?.codigo,
    data: presenca?.data,
  });

  // Derivado da lista de cronogramas; não precisa de state próprio nem de
  // sincronização manual. useMemo evita recalcular a cada render.
  const listValuesCronograma = useMemo(
    () =>
      listCronograma.map((c) => ({
        description: getCronogramaDescription(c),
        value: c.codigo,
      })),
    [listCronograma],
  );

  const handleChange = (event) => {
    setPresenca((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };

  const handleDataChange = (data) => {
    setPresenca((prevState) => ({
      ...prevState,
      data: dayjs(data),
      cronograma: null,
    }));
  };

  const handleCronogramaSelect = (event) => {
    setPresenca((prevState) => ({
      ...prevState,
      cronograma: listCronograma.find((c) => c.codigo === event.target.value),
    }));
  };

  const updatePresencas = (data) => {
    setPresencas((prevState) => ({
      ...prevState,
      content: prevState.content.map((ps) =>
        ps.codigo === data.codigo ? data : ps,
      ),
    }));
  };

  const clickSubmit = async (event) => {
    event.preventDefault();
    const eNovo = presenca.eNovo;
    setIsLoadingSave(true);
    try {
      const data = await save(presenca);
      if (!data) {
        errorMessage(MESSAGES.PRESENCA_SAVE_EMPTY);
        return;
      }
      if (data.error) {
        errorMessage(data.message);
        return;
      }
      toast.success(MESSAGES.PRESENCA_SAVED, { position: "bottom-left" });
      eNovo ? addPresencas(data) : updatePresencas(data);
    } catch (error) {
      errorMessage(error?.message ?? MESSAGES.PRESENCA_SAVE_ERROR);
    } finally {
      setIsLoadingSave(false);
    }
  };

  if (!presenca) return null;

  return (
    <Container sx={sx.paper} component={Paper}>
      <IconButton
        sx={sx.closeIcon}
        onClick={() => setPresenca(null)}
        size="small"
        aria-label="Fechar formulário de presença"
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <Grid container spacing={3}>
        <Grid size={6}>
          <Container sx={sx.paper} component={Paper} maxWidth="sm">
            <form onSubmit={clickSubmit}>
              <Grid container direction="column" spacing={1}>
                <Grid>
                  <TextFieldDate
                    required
                    label="Data"
                    value={presenca.data ?? null}
                    onChange={handleDataChange}
                  />
                </Grid>

                <CronogramaField
                  selected={presenca.cronograma}
                  options={listValuesCronograma}
                  isLoading={isLoadingCronograma}
                  disabled={!presenca.data}
                  onLoad={loadCronogramas}
                  onSelect={handleCronogramaSelect}
                />

                <Grid>
                  <TextField
                    variant="outlined"
                    margin="normal"
                    multiline
                    minRows={4}
                    fullWidth
                    label="Conteúdo"
                    name="conteudo"
                    onChange={handleChange}
                    value={presenca.conteudo ?? ""}
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                  />
                </Grid>

                <Grid>
                  <SubmitButton isLoading={isLoadingSave} />
                </Grid>
              </Grid>
            </form>
          </Container>
        </Grid>

        <Grid size={6}>
          {presenca.codigo ? (
            <PresencaCapituloTable presenca={presenca} />
          ) : null}
        </Grid>

        <Grid size={8}>
          <PresencaAlunoTableSwitch presenca={presenca} />
        </Grid>
      </Grid>
    </Container>
  );
}
